import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "About", path: "/about" },
  { label: "Locations", path: "/locations" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0C0C0C]/95 backdrop-blur-md border-b border-white/8"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-screen-xl mx-auto px-6 xl:px-10 flex items-center justify-between h-[72px]">

        {/* Logo — always white/inverted on dark background */}
        <Link to="/" className="flex items-center flex-shrink-0 cursor-pointer">
          <img
            src="https://storage.readdy-site.link/project_files/9feb0e10-6b46-4f7c-b05e-9daa3db5d31b/728c2118-680d-47e0-a8f6-f12254ff9a4b_kitchens-renovated.png?v=dd6fc74b8793d072f57ebfceca483cce"
            alt="Kitchen Renovations Perfected"
            className="h-12 w-auto object-contain brightness-0 invert opacity-95"
            style={{ maxWidth: "200px" }}
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-[11px] tracking-[0.16em] uppercase font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                location.pathname === link.path
                  ? "text-[#C9A961]"
                  : "text-white/70 hover:text-white"
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right side */}
        <div className="hidden lg:flex items-center gap-5">
          <a
            href="tel:5614416008"
            className="flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors duration-300 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-[#C9A961] text-sm" />
            </div>
            (561) 441-6008
          </a>
          <Link
            to="/locations"
            className="bg-[#C9A961] text-white text-[11px] tracking-[0.16em] uppercase font-semibold px-6 py-3 hover:bg-[#b8964f] transition-colors duration-300 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Free Consultation
          </Link>
        </div>

        {/* Mobile right */}
        <div className="lg:hidden flex items-center gap-4">
          <a
            href="tel:5614416008"
            className="flex items-center gap-1.5 text-xs font-medium text-[#C9A961]"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-fill text-base" />
            </div>
            <span className="hidden sm:inline">Call Now</span>
          </a>
          <button
            className="w-8 h-8 flex flex-col justify-center gap-[5px] cursor-pointer text-white"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block h-[1.5px] bg-current transition-all duration-300 ${menuOpen ? "rotate-45 translate-y-[6.5px]" : ""}`} />
            <span className={`block h-[1.5px] bg-current transition-all duration-300 ${menuOpen ? "opacity-0 w-0" : "w-full"}`} />
            <span className={`block h-[1.5px] bg-current transition-all duration-300 ${menuOpen ? "-rotate-45 -translate-y-[6.5px]" : ""}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu — dark to match */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-400 ${
          menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        } bg-[#0C0C0C] border-t border-white/8`}
      >
        <nav className="px-6 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-[11px] tracking-[0.16em] uppercase font-medium transition-colors cursor-pointer ${
                location.pathname === link.path ? "text-[#C9A961]" : "text-white/60 hover:text-white"
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <div className="h-px bg-white/8" />
          <a
            href="tel:5614416008"
            className="flex items-center gap-2 text-sm font-medium text-white/70 cursor-pointer"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-[#C9A961] text-sm" />
            </div>
            (561) 441-6008
          </a>
          <Link
            to="/locations"
            className="bg-[#C9A961] text-white text-[11px] tracking-[0.16em] uppercase font-semibold px-6 py-3 text-center whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Inter', sans-serif" }}
            onClick={() => setMenuOpen(false)}
          >
            Free Consultation
          </Link>
        </nav>
      </div>
    </header>
  );
}
